import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { makeStyles } from '@material-ui/styles';
import { Divider, Drawer } from '@material-ui/core';
import {
  Assignment as AssignmentIcon,
  Settings as SettingsIcon,
  PeopleAlt as PeopleAltIcon,
  Person as PersonIcon,
  // Dashboard as DashboardIcon
  AttachMoney as AttachMoneyIcon,
  Schedule as ScheduleIcon,
  PostAdd as PostAddIcon,
  // ChromeReaderMode as ChromeReaderModeIcon,
  ListAlt as ListAltIcon,
  Receipt as ReceiptIcon
} from '@material-ui/icons';

import * as selectorsAuth from '../../../../../../store/auth/reducer';
import * as selectorsModuleConfiguration from '../../../../../../store/moduleConfiguration/reducer';
import * as Role from '../../../../../../constants/role';

import Profile from './Profile';
import SidebarNav from './SidebarNav';
import { isUsuarioUsoMuto } from 'utils';
import { FOLHA_PONTO_OLD, FOLHA_PONTO, PROGRAMACAO_DIARIA } from 'constants/modules';

const useStyles = makeStyles(theme => ({
  drawer: {
    width: 240,
    [theme.breakpoints.up('lg')]: {
      marginTop: 64,
      height: 'calc(100% - 64px)',
      borderRight: 'none'
    }
  },
  root: {
    backgroundColor: theme.palette.bar.principal,
    display: 'flex',
    height: '150%',
    flexDirection: 'column',
    padding: theme.spacing(2)
  },
  divider: {
    margin: theme.spacing(2, 0),
    backgroundColor: theme.palette.bar.light
  }
}));

const renderFolhaPonto = (authData, moduleConfigurations) => {
  if (isUsuarioUsoMuto(authData.email)) return [];

  const options = [];
  const showOld = moduleConfigurations?.find(u => u.codigo === FOLHA_PONTO_OLD && u.userId === authData.id);
  if (showOld) {
    options.push({
      title: 'Folha de Ponto (Antigo)',
      href: '/folha-pontos',
      show: true,
      key: 'inspecao',
    });
  }

  const showNovo = moduleConfigurations?.find(u => u.codigo === FOLHA_PONTO && (u.userId === authData.id || !u.userId));
  if (showNovo) {
    options.push({
      title: 'Folha de Ponto',
      href: '/folha-pontos-v2',
      show: true,
      key: 'inspecao',
    });
  }
  
  return options;
}

const renderProgramacaoDiaria = (authData, moduleConfigurations) => {
  if (isUsuarioUsoMuto(authData.email)) return [];

  const options = [];

  const showModule = moduleConfigurations?.find(u => u.codigo === PROGRAMACAO_DIARIA && u.userId === authData.id);
  if (!isUsuarioUsoMuto(authData.email) && showModule) {
    options.push({
      title: 'Programação Diária',
      href: '/programacao-diaria',
      show: true,
      key: 'inspecao',
    });
  }
  
  return options;
}

const Sidebar = ({ open, variant, onClose, userRole, authData, moduleConfigurations }) => {
  const classes = useStyles();

  const pages = () => ([
    {
      title: 'Meu Perfil',
      icon: <PersonIcon />,
      href: '/meu-perfil',
      show: true,
    },
    {
      title: 'Inspeção',
      icon: <AssignmentIcon />,
      show: true,
      key: 'inspecao',
      isActiveCallback: () => {},
      options: [
        {
          title: 'NSIRs',
          href: '/nsirs',
          isActive: (match, location) => match || (location && location.pathname.includes('/nsir')),
          show: true,
          key: 'inspecao',
        },
        {
          title: 'Convocações',
          href: '/convocacoes',
          show: !isUsuarioUsoMuto(authData.email),
          key: 'inspecao',
        },
        {
          title: 'Equipamentos',
          href: '/equipamentos',
          show: process.env.REACT_APP_EQUIPAMENTO_VIEW.split(',').includes(authData.email) && !isUsuarioUsoMuto(authData.email),
          key: 'inspecao',
        },
        {
          title: 'Controle de Jornada',
          href: '/controle-frequencia',
          show: !isUsuarioUsoMuto(authData.email),
          key: 'inspecao',
        },
        ...renderFolhaPonto(authData, moduleConfigurations),
        ...renderProgramacaoDiaria(authData, moduleConfigurations),
        {
          title: 'Relatórios Digitadores',
          href: '/reports',
          show: (process.env.REACT_APP_USUARIO_VIEW_DIGITADOR_REPORT.split(',').includes(authData.email) || authData.role === Role.DIGITADOR) && !isUsuarioUsoMuto(authData.email),
          key: 'inspecao',
        },
      ]
    },
    {
      title: 'Financeiro',
      icon: <AttachMoneyIcon />,
      show: !isUsuarioUsoMuto(authData.email),
      key: 'financeiro',
      options: [
        {
          title: 'Aportes',
          href: '/aportes',
          isActive: (match, location) => match || (location && location.pathname.includes('/aporte')),
          show: userRole !== Role.CROQUI && userRole !== Role.DIGITADOR && !isUsuarioUsoMuto(authData.email),
          key: 'financeiro',
        },
        {
          title: 'Cartões',
          href: '/cartoes',
          show: (userRole === Role.SUPER_ADMIN || userRole === Role.ADMIN) && !isUsuarioUsoMuto(authData.email),
          key: 'financeiro',
        },
        // {
        //   title: 'Controle de Caixa',
        //   href: '/controle-caixa',
        //   show: userRole === Role.SUPER_ADMIN || userRole === Role.ADMIN
        // },
        {
          title: 'Notas',
          href: '/notas',
          show: userRole !== Role.CROQUI && userRole !== Role.DIGITADOR && !isUsuarioUsoMuto(authData.email),
          key: 'financeiro',
        },
        {
          title: 'Odômetros',
          href: '/odometros',
          show: !isUsuarioUsoMuto(authData.email),
          key: 'financeiro',
        },
        {
          title: 'Veículos',
          href: '/veiculos',
          show: (userRole === Role.SUPER_ADMIN || userRole === Role.ADMIN) && !isUsuarioUsoMuto(authData.email),
          key: 'financeiro',
        },
        {
          title: 'Inspeções Veiculares',
          href: '/inspecoes-veiculares',
          show: !isUsuarioUsoMuto(authData.email),
          key: 'financeiro',
        },
        {
          title: 'Olerite',
          href: '/olerites',
          show: (process.env.REACT_APP_OLERITE_VIEW || '').split(',').includes(authData.email),
          key: 'financeiro',
        },
      ]
    },
    {
      title: 'Estoque',
      icon: <PostAddIcon />,
      show: process.env.REACT_APP_ESTOQUE_VIEW.split(',').includes(authData.email),
      key: 'estoque',
      options: [
        {
          title: 'Estoque',
          href: '/estoques',
          show: process.env.REACT_APP_ESTOQUE_VIEW.split(',').includes(authData.email),
          key: 'estoque',
        },
        {
          title: 'Pedidos',
          href: '/estoque/pedidos',
          isActive: (match, location) => match || (location && location.pathname.includes('/estoque/pedidos')),
          show: process.env.REACT_APP_ESTOQUE_VIEW.split(',').includes(authData.email),
          key: 'estoque',
        },
        {
          title: 'Produtos',
          href: '/estoque/produtos',
          isActive: (match, location) => match || (location && location.pathname.includes('/estoque/produtos')),
          show: process.env.REACT_APP_ESTOQUE_VIEW.split(',').includes(authData.email),
          key: 'estoque',
        }
      ]
    },
    {
      title: 'Usuários',
      icon: <PeopleAltIcon />,
      show: !isUsuarioUsoMuto(authData.email),
      key: 'usuarios',
      options: [
        {
          title: 'Listagem',
          href: '/usuarios',
          show: true,
          key: 'usuarios',
        },
        {
          title: 'Documentos e Certificados',
          href: '/usuarios-documentos',
          show: authData.email === 'vinicius_admin' || authData.email === 'MARIA' || authData.email === 'REGIANE MARIA DE OLIVEIRA' || authData.email === 'JORDANIA FLORENCIO MOTA',
          key: 'usuarios',
        },
      ]
    },
    {
      title: 'Controle de Ponto',
      icon: <ScheduleIcon />,
      href: '/controle-ponto',
      show: process.env.REACT_APP_CONTROLE_PONTO_VIEW.split(',').includes(authData.email),
    },
    {
      title: 'Procedimentos e Normas',
      icon: <ListAltIcon />,
      href: '/procedimentos-normas',
      show: process.env.REACT_APP_PROCEDIMENTOS_NORMAS_VIEW.split(',').includes(authData.email), //!isUsuarioUsoMuto(authData.email),
    },
    {
      title: 'Configurações',
      icon: <SettingsIcon />,
      show: (userRole === Role.SUPER_ADMIN || userRole === Role.ADMIN) && !isUsuarioUsoMuto(authData.email),
      key: 'configurations',
      options: [
        {
          title: 'Documentos',
          href: '/documentos',
          show: true,
          key: 'configurations',
        },
        {
          title: 'Malhas',
          href: '/malhas',
          show: true,
          key: 'configurations',
        },
        {
          title: 'Gerências',
          href: '/gerencias',
          show: true,
          key: 'configurations',
        },
        {
          title: 'Polos',
          href: '/polos',
          show: true,
          key: 'configurations',
        },
        {
          title: 'Subestações',
          href: '/subestacoes',
          show: true,
          key: 'configurations',
        },
        {
          title: 'Alimentadores',
          href: '/alimentadores',
          show: true,
          key: 'configurations',
        }
      ]
    },
  ]);

  return (
    <Drawer
      anchor="left"
      classes={{ paper: classes.drawer }}
      onClose={onClose}
      open={open}
      variant={variant}
    >
      <div className={classes.root}>
        <Profile />
        <Divider className={classes.divider} />
        <SidebarNav pages={pages()}/>
      </div>
    </Drawer>
  );
};

Sidebar.propTypes = {
  className: PropTypes.string,
  onClose: PropTypes.func,
  open: PropTypes.bool.isRequired,
  variant: PropTypes.string.isRequired
};

const mapStateToProps = state => ({
  userRole: selectorsAuth.getRole(state),
  authData: selectorsAuth.getAuth(state),
  moduleConfigurations: selectorsModuleConfiguration.getModuleConfigurations(state),
});

export default connect(mapStateToProps)(Sidebar);